// Form management utilities for NewShipmentModal
/**
 * Creates dynamic step names based on shipment mode
 */
export const createStepNames = (t: any) => {
  return computed(() => [
    { key: "sender", label: t("set_sender") },
    { key: "receiver", label: t("set_receiver") },
    { key: "dimensions", label: t("specify_dimensions") },
    { key: "carrier_product_service", label: t("select_carrier_product") },
    { key: "order_lines", label: t("add_order_lines") },
    { key: "dangerous_goods", label: t("dangerous_goods") },
    { key: "services", label: t("select_extra_services") },
    { key: "insurance", label: t("add_insurance") },
    { key: "summary", label: t("summary") },
    { key: "return_products", label: t("select_carrier_product") },
  ]);
};

/**
 * Creates the necessary computed properties for form UI management
 */
export const createFormUIControls = (
  currentStepIdx: Ref<number>,
  stepCounter: Ref<number>,
  currentSchema: ComputedRef<any>,
  isValid: Ref<boolean>,
  isDirty: Ref<boolean>,
  values: any,
  t: any
) => {
  // Last step check
  const isLastStep = computed(() => {
    return currentStepIdx.value === stepCounter.value - 1;
  });

  // Has previous button check
  const hasPrevious = computed(() => {
    return currentStepIdx.value > 0 && canGoBack.value !== false;
  });

  // Skip button visibility
  const hasSkip = computed(() => {
    return currentSchema.value?.spec?.meta?.skip;
  });

  // Can go back to previous step
  const canGoBack = computed<boolean>(() => {
    return currentSchema.value?.spec?.meta?.canGoBack ?? true;
  });

  // Continue button visibility
  const hasContinueButton = computed(() => {
    return currentSchema.value?.spec?.meta?.continueButton !== false;
  });

  // Continue button text
  const continueButtonText = computed(() => {
    if (isLastStep.value) {
      return t("create_shipment");
    }
    const key = currentSchema.value?.spec?.meta?.continueButtonText;
    return key ? t(key) : t("continue");
  });

  // Current step is empty check
  const currentStepIsEmpty = computed(() => {
    return Object.keys(values).length === 0;
  });

  // Can submit form check
  const canSubmit = computed(() => {
    // Always allow submission if the form is valid, regardless of dirty state
    // This helps with the save_sender/save_receiver issue in return mode
    return isValid.value;
  });

  return {
    isLastStep,
    hasPrevious,
    hasSkip,
    canGoBack,
    hasContinueButton,
    continueButtonText,
    currentStepIsEmpty,
    canSubmit,
  };
};

/**
 * Skip management for validation steps
 */
export const skipStep = (validationSchema: Ref<any[]>, currentStepIdx: Ref<number>) => {
  if (!validationSchema.value) return;

  for (let i = currentStepIdx.value + 1; i < validationSchema.value.length; i++) {
    if (!validationSchema.value?.[i]?.spec?.meta?.autoskip) {
      currentStepIdx.value = i;
      return;
    }
  }
};

/**
 * Go to previous step
 */
export const goToPrevStep = (
  currentSchema: ComputedRef<any>,
  currentStepIdx: Ref<number>,
  validationSchema: Ref<any[]>
) => {
  if (currentSchema.value?.spec?.meta?.onPrev) {
    currentSchema.value.spec.meta.onPrev();
    return;
  }

  if (currentStepIdx.value === 0 || !validationSchema.value) {
    return;
  }

  for (let i = currentStepIdx.value - 1; i >= 0; i--) {
    if (!validationSchema.value[i]?.spec?.meta?.autoskip) {
      currentStepIdx.value = i;
      return;
    }
  }
};

/**
 * Go to the last step
 */
export const goToLastStep = (currentStepIdx: Ref<number>, stepCounter: Ref<number>) => {
  if (stepCounter.value > 0) {
    currentStepIdx.value = stepCounter.value - 1;
  }
};

/**
 * Go to a step by key name
 */
export const goToStepByKeyName = (key: string, validationSchema: Ref<any[]>, currentStepIdx: Ref<number>) => {
  if (!validationSchema.value) return;

  for (let i = 0; i < validationSchema.value.length; i++) {
    if (validationSchema.value[i]?.spec?.meta?.key === key) {
      currentStepIdx.value = i;
      return;
    }
  }
};

/**
 * Controls insurance step auto-skipping
 */
export const skipInsurance = (validationSchema: Ref<any[]>, val = false) => {
  const insuranceStepIdx = validationSchema.value.findIndex((step) => step.spec.meta.key === "insurance");
  if (insuranceStepIdx >= 0) {
    validationSchema.value[insuranceStepIdx].spec.meta.autoskip = val;
  }
};

/**
 * Updates validation schema with extra service fields
 */
export const updateValidation = (
  val: { fields: object },
  validationSchema: Ref<any[]>,
  injectValidationFields: any,
  formValues?: any
) => {
  const servicesStepIdx = validationSchema.value.findIndex((step) => step.spec.meta.key === "services");
  if (servicesStepIdx >= 0) {
    injectValidationFields(val.fields, validationSchema.value, servicesStepIdx, "services");

    const hasNestedObjects = () => {
      const serviceFields = validationSchema.value[servicesStepIdx].fields.services.fields;
      for (const key in serviceFields) {
        if (typeof serviceFields[key] === "object" && Object.keys(serviceFields[key].fields).length > 0) {
          return true;
        }
      }
      return false;
    };

    const hasSelectedServices = () => {
      const services = formValues?.services ?? {};
      return Object.values(services).some((s: any) => s?.isSet);
    };

    validationSchema.value[servicesStepIdx].spec.meta.skip = !hasNestedObjects() && !hasSelectedServices();
  }
};
